import "flag-icons";

import clsx from "clsx";
import { motion } from "framer-motion";
import { memo, useCallback } from "react";
import CurrencyFlag from "react-currency-flags";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "src/helpers/utils";
import useMobile from "src/hooks/useMobile";
import useColors from "src/hooks/useColors";
import CurrencyBadge from "../CurrencyBadge/CurrencyBadge";
import useUiPreferences from "src/store/useUiPreferences";

interface IAvailablePerCurrencyCard {
  code: string;
  name: string;
  symbol: string;
  total: number;
  inactiveTotal?: number | null;
  animationIndex: number;
  vertical?: boolean;
}

const AvailablePerCurrencyCard: React.FC<IAvailablePerCurrencyCard> = ({
  code,
  name,
  symbol,
  total,
  inactiveTotal = null,
  animationIndex,
  vertical = false,
}) => {
  const enableAnimations = useUiPreferences(
    (state) => state.misc.enableComplexAnimations,
  );
  const { isMobile } = useMobile();
  const { getBalanceColors } = useColors();
  const { t } = useTranslation();

  const hasDecimals = useCallback((value: any) => {
    return value % 1 !== 0;
  }, []);

  const FlagCard = useCallback(() => {
    if (isMobile) {
      return <CurrencyFlag currency={code} size="sm" className="rounded" />;
    }

    return (
      <div
        className={clsx([
          "items-center",
          "justify-center",
          "inline-flex",
          "rounded",
          "!h-[40px]",
          "!w-[40px]",
          "!min-h-[40px]",
          "!min-w-[40px]",
          vertical && [
            "md:!h-[40px]",
            "md:!w-[50px]",
            "md:!min-h-[40px]",
            "md:!min-w-[50px]",
            // "bg-white",
            // "dark:bg-gray-900",
          ],
          !vertical && [
            "shadow",
            "md:!h-[50px]",
            "md:!w-[50px]",
            "md:!min-h-[50px]",
            "md:!min-w-[50px]",
            "bg-gray-200/50",
            "dark:bg-gray-900/50",
          ],
        ])}
      >
        <CurrencyFlag currency={code} size="xl" className="rounded" />
      </div>
    );
  }, [isMobile, code, vertical]);

  const Total = useCallback(() => {
    return (
      <div
        className={clsx([
          "flex",
          "flex-col",
          "items-start",
          !vertical && "md:items-end",
          vertical && "md:items-center",
          "gap-1",
        ])}
      >
        <CurrencyBadge
          value={total}
          currencySymbol={symbol || "?"}
          kind="DIFFERENCE"
        />

        {inactiveTotal !== null && (
          <>
            <div
              className={clsx(["text-xs", "cursor-help"])}
              title={t("* Monto en cuentas inactivas")}
            >
              * {formatCurrency({ value: inactiveTotal, currency: symbol })}
            </div>
          </>
        )}
      </div>
    );
  }, [
    total,
    symbol,
    getBalanceColors,
    hasDecimals,
    inactiveTotal,
    t,
    isMobile,
    vertical,
  ]);

  return (
    <motion.div
      className={clsx([
        "flex",
        "flex-col",
        "md:flex-row",
        vertical && ["md:flex-col", "md:items-center", "md:gap-1"],
        !vertical && ["md:flex-row", "md:items-center", "md:gap-4"],
        "gap-4",
        "justify-between",
        "items-start",
        // "bg-gray-50/50 dark:bg-gray-900/50",
        "p-2",
        "md:p-2",
        "rounded",
        "border",
        "border-gray-50",
        "dark:border-gray-900/50",
        "hover:bg-gray-100",
        "hover:dark:bg-gray-900",
        "relative",
      ])}
      key={code}
      {...(enableAnimations && {
        initial: {
          opacity: 0,
          // x: animationIndex % 2 === 0 ? -100 : 100,
        },
        animate: { opacity: 1 },
        transition: {
          duration: 0.2,
          ease: "easeOut",
          delay: animationIndex * 0.1,
        },
      })}
    >
      <div
        className={clsx([
          "flex",
          "flex-row",
          "md:flex-col",
          "gap-2",
          "md:gap-0",
          "items-center",
          "md:items-start",
        ])}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "md:gap-1",
            "items-start",
            "truncate",
          ])}
        >
          <div
            className={clsx([
              "flex",
              vertical && ["flex-col"],
              !vertical && ["flex-row"],
              "gap-2",
              "items-center",
            ])}
          >
            {!isMobile && <FlagCard />}
            <div
              className={clsx([
                "flex",
                "flex-col",
                !vertical && ["items-start"],
                vertical && ["items-center"],
                "gap-0",
                "md:gap-0",
              ])}
            >
              <span
                className={clsx([
                  "font-medium",
                  "md:text-sm",
                  "text-sm",
                  "hidden",
                  "md:hidden",
                ])}
              >
                {code}
              </span>
              {isMobile ? (
                <div
                  className={clsx([
                    "flex",
                    "flex-row",
                    "gap-1",
                    "items-center",
                    "truncate",
                  ])}
                >
                  <div className="shrink-0">
                    <FlagCard />
                  </div>
                  <div
                    className={clsx([
                      "text-xs",
                      "truncate",
                      "md:text-xs",
                      "font-medium",
                    ])}
                  >
                    {name}
                  </div>
                </div>
              ) : (
                <div
                  className={clsx([
                    "md:text-xs",
                    "text-xs",
                    "truncate",
                    "font-medium",
                  ])}
                >
                  {name}
                </div>
              )}

              {isMobile && <Total />}
            </div>
          </div>
        </div>
      </div>
      {!isMobile && <Total />}
    </motion.div>
  );
};

export default memo(AvailablePerCurrencyCard);
